/** GPT Image2：单价 = 分辨率基础价 + 参考图数量 × 0.1 */
const LM_GPT_IMAGE2_REF_UNIT_PRICE = 0.1;

export function getLmBaseURL() {
  return (process.env.LM_BASE_URL || "").replace(/\/+$/, "");
}

export function lmApiResponseOk(body) {
  if (!body || typeof body !== "object") return false;
  const s = body.status;
  const c = body.code;
  return s === 1 || s === "1" || Number(s) === 1 || c === 1 || c === "1" || Number(c) === 1;
}

/** LM / AIGC 接口登录过期（与控制台 main.js 判断一致） */
export function isLmTokenExpiredPayload(payload) {
  const p = payload && typeof payload === "object" ? payload : {};
  const status = p.status;
  if (status === -2) return true;
  if (status !== 0) return false;
  const msg = String(p.message || p.msg || p.error_msg || "").toLowerCase();
  return msg.indexOf("token") !== -1 && (msg.indexOf("过期") !== -1 || msg.indexOf("expired") !== -1);
}

/** 从 LM 接口响应或 axios 错误中提取可读错误文案 */
export function pickLmApiErrorMsg(payload) {
  const p = payload && typeof payload === "object" ? payload : {};
  const nested = p.data && typeof p.data === "object" ? p.data : {};
  const candidates = [p.msg, p.error_msg, p.message, nested.msg, nested.error_msg, nested.message];
  for (let i = 0; i < candidates.length; i += 1) {
    const s = candidates[i] != null ? String(candidates[i]).trim() : "";
    if (s) return s;
  }
  return "";
}

export function pickAxiosLmErrorMsg(error, fallback) {
  if (error && error.message === "登录过期") return "";
  const data = error && error.response && error.response.data;
  const msg = pickLmApiErrorMsg(data);
  if (msg) return msg;
  return fallback || "";
}

export function extractAigcTaskId(body) {
  const data = body && body.data;
  if (!data || typeof data !== "object") return "";
  const id =
    data.aigc_task_id ||
    data.aigcTaskId ||
    data.TaskId ||
    data.taskId ||
    data.task_id ||
    data.id;
  return id != null ? String(id).trim() : "";
}

export function normalizeLmMediaDisplayUrl(path, lmBaseUrl) {
  let s = path != null ? String(path).trim() : "";
  if (!s) return "";
  if (/^(data:|blob:)/i.test(s)) return s;
  if (/^\/\//.test(s)) return `https:${s}`;
  if (/^https?:\/\//i.test(s)) return s;
  if (/^asset:\/\//i.test(s)) return "";
  const base = (lmBaseUrl || "").replace(/\/+$/, "");
  if (!base) return s;
  if (s.charAt(0) !== "/") s = `/${s}`;
  return `${base}${s}`;
}

function lmModelTextParts(m) {
  if (!m) return [];
  return [m.version, m.name, m.id].map((x) =>
    String(x != null ? x : "")
      .trim()
      .toLowerCase()
  );
}

function isLmGptImage2Model(m) {
  const parts = lmModelTextParts(m);
  return parts.some(
    (p) =>
      p.startsWith("image2_") ||
      p.includes("image2") ||
      p.includes("gpt image2") ||
      p.includes("gpt image 2") ||
      p === "og"
  );
}

function countRefImages(refImages) {
  return (refImages || []).filter((x) => x && (x.media_url || x.file_id)).length;
}

function calcGptImage2UnitPrice(basePrice, refCount, model) {
  const base = Number(basePrice) || 0;
  if (!isLmGptImage2Model(model)) return base;
  return base + Math.max(0, Number(refCount) || 0) * LM_GPT_IMAGE2_REF_UNIT_PRICE;
}

export function mapResolutionItemToString(r) {
  if (r == null) return "";
  if (typeof r === "string" || typeof r === "number") return String(r).trim();
  if (typeof r === "object") {
    const pick = r.resolution || r.value || r.name || r.label;
    return pick != null ? String(pick).trim() : "";
  }
  return "";
}

export function mapAspectRatioItemToString(r) {
  if (r == null) return "";
  if (typeof r === "string" || typeof r === "number") return String(r).trim();
  if (typeof r === "object") {
    const pick = r.aspect_ratio || r.ratio || r.value || r.name || r.label;
    return pick != null ? String(pick).trim() : "";
  }
  return "";
}

export function findPriceItemByResolution(model, resolution) {
  if (!model) return null;
  const res = String(resolution != null ? resolution : "").trim();
  const resArr = Array.isArray(model.resolution) ? model.resolution : [];
  if (resArr.length && typeof resArr[0] === "object") {
    const item = resArr.find(
      (p) => String(p.resolution || p.value || p.name || "").trim() === res
    );
    if (item) return item;
  }
  if (Array.isArray(model.price)) {
    return model.price.find((p) => String(p.resolution || "").trim() === res) || null;
  }
  return null;
}

export function getT2IPriceDisplayText(model, resolution, refImages) {
  const item = findPriceItemByResolution(model, resolution);
  if (!item) return "0元/张";
  const priceVal =
    item.price != null ? item.price : item.unit_price != null ? item.unit_price : null;
  if (priceVal == null) return "0元/张";
  const unitPrice = calcGptImage2UnitPrice(priceVal, countRefImages(refImages), model);
  const u = item.unit || "元/张";
  const price = Number(unitPrice);
  if (!Number.isFinite(price)) return `0${u}`;
  const text = Number.isInteger(price) ? String(price) : price.toFixed(2);
  return `${text}${u}`;
}

function normalizeOneAigcModel(x, extra = {}) {
  return {
    id: x.id,
    name: x.name,
    version: x.version,
    price: x.price,
    resolution: x.resolution,
    aspect_ratio: x.aspect_ratio,
    remark: x.remark,
    max_ref: x.max_ref != null ? Math.max(0, Number(x.max_ref) || 0) : 1,
    max_num: x.max_num != null ? Number(x.max_num) : 3,
    ref_requirement: x.ref_requirement != null ? String(x.ref_requirement) : "",
    ...extra,
  };
}

export function normalizeAigcModels(payload) {
  const list = Array.isArray(payload) ? payload : [];
  return list.map((x) => normalizeOneAigcModel(x));
}

export function normalizeVideoModels(payload) {
  const list = Array.isArray(payload) ? payload : [];
  return list.map((x) =>
    normalizeOneAigcModel(x, {
      duration: x.duration,
      reference: x.reference,
      audio: x.audio,
      discount: x.discount,
    })
  );
}

function lmModelNameParts(m) {
  if (!m) return [];
  return [m.name, m.id, m.version].map((part) =>
    String(part != null ? part : "")
      .trim()
      .toLowerCase()
  );
}

export function isSeedanceLikeModel(m) {
  const parts = lmModelNameParts(m);
  return parts.some((p) => p.includes("seedance") || p.includes("cdance"));
}

export function isCdanceImageUploadModel(m) {
  const parts = lmModelNameParts(m);
  return parts.some(
    (p) =>
      p.includes("2.0-pro") ||
      p.includes("2.0_pro") ||
      p.includes("cdance") ||
      (p.includes("seedance") && p.includes("2.0"))
  );
}

export function extractAssertIdFromPossibleAssetRef(url) {
  const s = String(url || "").trim();
  if (!s) return "";
  const m = s.match(/^asset:\/\/\/?([^/?#]+)/i);
  return m && m[1] ? String(m[1]).trim() : "";
}

export function assertIdFromMediaItem(it) {
  if (!it || typeof it !== "object") return "";
  const raw =
    it.asset_id != null
      ? it.asset_id
      : it.assert_id != null
        ? it.assert_id
        : it.assertId != null
          ? it.assertId
          : it.assertID != null
            ? it.assertID
            : "";
  return raw != null && String(raw).trim() !== "" ? String(raw).trim() : "";
}

export function toAssetRef(assertId) {
  const raw = assertId != null ? String(assertId).trim() : "";
  if (!raw) return "";
  if (/^asset:\/\//i.test(raw)) return raw;
  return `asset://${raw}`;
}

export function resolveSeedanceImageRefUrl(it, model) {
  const url = it && (it.media_url || it.url) ? String(it.media_url || it.url).trim() : "";
  if (!url) return "";
  const aidFromField = assertIdFromMediaItem(it);
  if (isCdanceImageUploadModel(model)) {
    return aidFromField ? toAssetRef(aidFromField) : url;
  }
  const aid = aidFromField || extractAssertIdFromPossibleAssetRef(url) || "";
  return aid ? toAssetRef(aid) : url;
}

export function getRefKind(item) {
  if (!item || typeof item !== "object") return "image";
  const raw = item.refKind != null ? item.refKind : item.ref_kind != null ? item.ref_kind : item.kind;
  const k = String(raw != null ? raw : "")
    .trim()
    .toLowerCase();
  if (k === "video" || k === "audio" || k === "image") return k;
  return "image";
}

export function parseV2iRefConfigs(model) {
  const refs = model && model.reference;
  const refArr = Array.isArray(refs) ? refs : [];
  let refNames = refArr.map((r) => (r && r.name) || "").filter(Boolean);
  let configs = refArr.map((r) => ({
    name: r && r.name,
    min_ref: r && r.min_ref != null ? Math.max(0, Number(r.min_ref) || 0) : 0,
    max_ref: r && r.max_ref != null ? Math.max(0, Number(r.max_ref) || 0) : 3,
    min_ref_image: r && r.min_ref_image != null ? Math.max(0, Number(r.min_ref_image) || 0) : null,
    max_ref_image: r && r.max_ref_image != null ? Math.max(0, Number(r.max_ref_image) || 0) : null,
    min_ref_video: r && r.min_ref_video != null ? Math.max(0, Number(r.min_ref_video) || 0) : null,
    max_ref_video: r && r.max_ref_video != null ? Math.max(0, Number(r.max_ref_video) || 0) : null,
    min_ref_audio: r && r.min_ref_audio != null ? Math.max(0, Number(r.min_ref_audio) || 0) : null,
    max_ref_audio: r && r.max_ref_audio != null ? Math.max(0, Number(r.max_ref_audio) || 0) : null,
  }));
  if (refNames.length === 0) {
    refNames = ["参考图"];
    configs = [{ name: "参考图", min_ref: 0, max_ref: 1 }];
  }
  return { refNames, configs };
}

export function getSeedanceRefLimits(cfg) {
  const c = cfg || {};
  const toNum = (v, d) => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.max(0, n) : d;
  };
  return {
    image: { min: toNum(c.min_ref_image, 0), max: toNum(c.max_ref_image, 0) },
    video: { min: toNum(c.min_ref_video, 0), max: toNum(c.max_ref_video, 0) },
    audio: { min: toNum(c.min_ref_audio, 0), max: toNum(c.max_ref_audio, 0) },
  };
}

function v2iPriceResolutionMatches(sizeStr, resolutionField) {
  const strip = (val) =>
    String(val != null ? val : "")
      .trim()
      .toLowerCase()
      .replace(/p$/i, "");
  const a = strip(sizeStr);
  const b = strip(resolutionField);
  return a && b && a === b;
}

function getV2IPriceRowsFromModel(m) {
  if (!m || typeof m !== "object") return [];
  if (Array.isArray(m.price) && m.price.length) return m.price;
  const resArr = Array.isArray(m.resolution) ? m.resolution : [];
  const rows = [];
  resArr.forEach((entry) => {
    if (!entry || typeof entry !== "object") return;
    const pv = entry.price != null ? entry.price : entry.unit_price != null ? entry.unit_price : null;
    if (pv == null) return;
    rows.push({
      resolution: entry.resolution != null ? entry.resolution : entry.value != null ? entry.value : entry.name,
      price: pv,
      unit: entry.unit,
      resolution_audio: entry.resolution_audio != null ? entry.resolution_audio : entry.resolutionAudio,
    });
  });
  return rows;
}

export function getV2IPriceDisplayText(model, size, audioEnabled) {
  const approx = isSeedanceLikeModel(model) ? "约" : "";
  const rows = getV2IPriceRowsFromModel(model);
  if (!model || !rows.length) return `${approx}0元/秒`;
  const resKey = (size || "") + "p";
  const audioOn = !!(audioEnabled && model && model.audio === 1);
  const norm = (val) => String(val || "").trim().toLowerCase();
  const matchResolutionRow = (p) =>
    p &&
    p.resolution != null &&
    (norm(p.resolution) === norm(resKey) || v2iPriceResolutionMatches(size, p.resolution));
  const matchAudioRow = (p) => {
    const ra = p && (p.resolution_audio != null ? p.resolution_audio : p.resolutionAudio);
    return ra != null && (norm(ra) === norm(resKey) || v2iPriceResolutionMatches(size, ra));
  };
  let priceItem = audioOn ? rows.find((p) => matchAudioRow(p)) : null;
  if (!priceItem) priceItem = rows.find((p) => matchResolutionRow(p));
  if (!priceItem && rows.length === 1) priceItem = rows[0];
  const discount = Number(model.discount) || 1;
  const unitPrice = priceItem ? Number(priceItem.price) || 0 : 0;
  const unit = priceItem && priceItem.unit && String(priceItem.unit).includes("秒") ? priceItem.unit : "元/秒";
  return `${approx}${(unitPrice * discount).toFixed(2)}${unit}`;
}

function buildWorkspaceVideoOutputConfig(model, { duration, size, ratio, audioEnabled }) {
  const durationNum = parseInt(String(duration || "").replace(/s$/i, ""), 10) || 5;
  const resolution = (size || "720") + (String(size || "").match(/p$/i) ? "" : "p");
  if (isSeedanceLikeModel(model)) {
    return {
      duration: durationNum,
      generate_audio: !!(audioEnabled && model && model.audio === 1),
      input_video_duration: 0,
      ratio: ratio || "16:9",
      resolution,
    };
  }
  return {
    AspectRatio: ratio || "16:9",
    Duration: durationNum,
    Resolution: resolution,
    AudioGeneration: audioEnabled && model && model.audio === 1 ? "Enabled" : "Disabled",
    StorageMode: "Temporary",
  };
}

function buildWorkspaceV2IReference(model, { refImageType, firstFrame, lastFrame, uploadedImages }) {
  const reference = { audio_url: [], image_url: [], video_url: [] };
  if (refImageType === "首尾帧") {
    const first = firstFrame && firstFrame.media_url ? resolveSeedanceImageRefUrl(firstFrame, model) : "";
    const last = lastFrame && lastFrame.media_url ? resolveSeedanceImageRefUrl(lastFrame, model) : "";
    if (first) reference.first_frame = first;
    if (last) reference.last_frame = last;
    return reference;
  }
  const refs = (uploadedImages || []).filter((x) => x && x.media_url);
  refs.forEach((it) => {
    const url = String(it.media_url).trim();
    if (!url) return;
    const k = getRefKind(it);
    if (k === "video") reference.video_url.push(url);
    else if (k === "audio") reference.audio_url.push(url);
    else {
      reference.image_url.push(isSeedanceLikeModel(model) ? resolveSeedanceImageRefUrl(it, model) : url);
    }
  });
  return reference;
}

function buildWorkspaceV2IFileInfos(model, { refImageType, firstFrame, lastFrame, uploadedImages }) {
  if (!model) return { file_infos: [], last_frame_url: "" };
  let file_infos = [];
  let last_frame_url = "";
  if (refImageType === "首尾帧") {
    if (firstFrame && firstFrame.media_url) {
      file_infos = [
        {
          Category: "Image",
          ObjectId: "",
          Type: "Url",
          Url: String(firstFrame.media_url).trim(),
        },
      ];
    }
    last_frame_url =
      lastFrame && lastFrame.media_url ? String(lastFrame.media_url).trim() : "";
    return { file_infos, last_frame_url };
  }
  const refs = (uploadedImages || []).filter((x) => x && x.media_url);
  const seedanceMixed = isSeedanceLikeModel(model) && refImageType !== "首尾帧";
  if (seedanceMixed) {
    const typeCounters = { image: 0, video: 0, audio: 0 };
    const catMap = { image: "Image", video: "Video", audio: "Audio" };
    const idPrefix = { image: "图片", video: "视频", audio: "音频" };
    file_infos = refs.map((img) => {
      const kind = getRefKind(img);
      typeCounters[kind] = (typeCounters[kind] || 0) + 1;
      const n = typeCounters[kind];
      return {
        Category: catMap[kind] || "Image",
        ObjectId: `${idPrefix[kind] || "图片"}${n}`,
        Type: "Url",
        Url: String(img.media_url).trim(),
      };
    });
  } else {
    file_infos = refs.map((img, idx) => ({
      Category: "Image",
      ObjectId: `图片${idx + 1}`,
      Type: "Url",
      Url: String(img.media_url).trim(),
    }));
  }
  return { file_infos, last_frame_url };
}

export function buildWorkspaceVideoPayload({
  prompt,
  model,
  refImageType,
  firstFrame,
  lastFrame,
  uploadedImages,
  duration,
  size,
  ratio,
  audioEnabled,
}) {
  if (!model) return null;
  const base = {
    task_type: "video",
    prompt: (prompt || "").trim(),
    model_id: model.id || "",
    model_name: model.name || model.id || "",
    model_version: model.version != null ? String(model.version) : "",
    output_config: buildWorkspaceVideoOutputConfig(model, { duration, size, ratio, audioEnabled }),
  };
  if (isSeedanceLikeModel(model)) {
    return {
      ...base,
      reference: buildWorkspaceV2IReference(model, {
        refImageType,
        firstFrame,
        lastFrame,
        uploadedImages,
      }),
    };
  }
  const { file_infos, last_frame_url } = buildWorkspaceV2IFileInfos(model, {
    refImageType,
    firstFrame,
    lastFrame,
    uploadedImages,
  });
  return {
    ...base,
    file_infos,
    ...(last_frame_url ? { last_frame_url } : {}),
  };
}

export function formatModelLabel(m) {
  if (!m) return "";
  const base = m.name || m.id || "";
  const v = m.version;
  if (v != null && String(v).trim() !== "") return `${base} (${v})`;
  return base;
}

export function buildWorkspaceImagePayload({ prompt, model, refImages, size, ratio, sizeOptions }) {
  if (!model) return null;
  const refs = (refImages || []).filter((x) => x && (x.file_id || x.media_url));
  const image_url = refs
    .map((img) => (img.media_url ? String(img.media_url).trim() : ""))
    .filter(Boolean);
  const resolutionStr = mapResolutionItemToString(size) || String(size || "").trim();
  const aspectRatioStr = mapAspectRatioItemToString(ratio) || String(ratio || "").trim();
  const supportsResolution = Array.isArray(sizeOptions) && sizeOptions.length > 0;
  return {
    task_type: "image",
    prompt: (prompt || "").trim(),
    model_id: model.id,
    model_name: model.name || model.id,
    model_version: model.version != null ? String(model.version) : "",
    file_infos: refs
      .filter((x) => x.media_url)
      .map((x) => ({ Type: "Url", Url: String(x.media_url).trim() }))
      .filter((fi) => fi.Url),
    reference: {
      audio_url: [],
      image_url,
      video_url: [],
    },
    output_config: {
      StorageMode: "Temporary",
      Resolution: supportsResolution ? resolutionStr : "",
      ...(aspectRatioStr ? { AspectRatio: aspectRatioStr } : {}),
    },
  };
}

export function parseWorkspaceTaskPoll(body) {
  if (!body || typeof body !== "object") {
    return { state: "failed", msg: "任务查询失败" };
  }
  if (body.status === 0) {
    return { state: "failed", msg: body.msg || body.error_msg || "任务失败" };
  }
  const data = body.data && typeof body.data === "object" ? body.data : {};
  const taskStatus = data.task_status != null ? String(data.task_status) : "";
  if (taskStatus === "FINISH") {
    return {
      state: "success",
      url: data.result_url != null ? String(data.result_url).trim() : "",
      id: data.id,
    };
  }
  return { state: "pending" };
}

export function mapWorkspaceHistoryRecord(row, lmBase) {
  if (!row || typeof row !== "object") return null;
  if (row.aigc_task_status !== "SUCCESS") return null;
  const resultUrl = row.result_url != null ? String(row.result_url).trim() : "";
  if (!resultUrl) return null;
  const url = normalizeLmMediaDisplayUrl(resultUrl, lmBase);
  if (!url) return null;
  const taskId = row.aigc_task_id != null ? String(row.aigc_task_id).trim() : "";
  if (!taskId) return null;
  const taskType = row.task_type != null ? String(row.task_type) : "";
  const generatedAt =
    row.update_time != null && String(row.update_time).trim() !== ""
      ? String(row.update_time).trim()
      : row.create_time != null
        ? String(row.create_time).trim()
        : "";
  return {
    url,
    taskId,
    id: row.id,
    taskType: taskType === "video" ? "video" : "image",
    generatedAt,
  };
}

function lmModelTextBlob(m) {
  if (!m) return "";
  return lmModelNameParts(m).filter(Boolean).join(" ");
}

function isLmSeedanceProModel(m) {
  const parts = lmModelNameParts(m);
  return parts.some((p) => p.includes("2.0-pro") || p.includes("2.0_pro"));
}

function isLmSeedance20FastInBlob(blob) {
  const b = String(blob || "").toLowerCase();
  return b.includes("2.0-fast") || b.includes("2.0_fast") || /2[._\-]0[\-_]?fast/.test(b);
}

function isLmSeedance20GalleryInBlob(blob) {
  const b = String(blob || "").toLowerCase();
  if (!b) return false;
  if (b.includes("2.0-pro") || b.includes("2.0_pro") || /2[._\-]0[\-_]?pro/.test(b)) return false;
  if (isLmSeedance20FastInBlob(b)) return true;
  return /2[._\-]0/.test(b) || /seedance\s*2[._\-]?0/.test(b) || /seedance2[._\-]?0/.test(b);
}

/** Seedance 2.0 / 2.0-fast：媒资弹窗展示火山人像库 */
export function isLmSeedanceVolcanoGalleryModel(m) {
  if (!m) return false;
  const blob = lmModelTextBlob(m);
  const id = String(m.id != null ? m.id : "").toLowerCase();
  const name = String(m.name != null ? m.name : "").toLowerCase();
  const isSeedance =
    id.includes("seedance") || name.includes("seedance") || blob.includes("seedance");
  const isCdance = (id.includes("cdance") || name.includes("cdance")) && !isSeedance;
  if (!isSeedance || isCdance) return false;
  if (isLmSeedanceProModel(m)) return false;
  return isLmSeedance20GalleryInBlob(blob);
}

export function refItemFromMediaSelect(s, extra = {}) {
  const url = s && (s.cover || s.media_url) ? String(s.cover || s.media_url).trim() : "";
  const item = {
    media_url: url,
    file_id: s && s.id != null ? String(s.id) : "",
    ...extra,
  };
  const assertId = assertIdFromMediaItem(s);
  if (assertId) item.asset_id = assertId;
  if (s && (s.human_gallery === "real" || s.human_gallery === "virtual")) {
    item.human_gallery = s.human_gallery;
  }
  return item;
}

export function saveBlobAsFile(blob, filename) {
  const name = filename || "download";
  const nav = typeof navigator !== "undefined" ? navigator : null;
  if (nav && typeof nav.msSaveOrOpenBlob === "function") {
    nav.msSaveOrOpenBlob(blob, name);
    return;
  }
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = name;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 2500);
}

export function guessExtFromMime(mime, fallbackExt) {
  const m = (mime || "").toString().toLowerCase();
  if (!m) return fallbackExt || "";
  if (m.indexOf("image/") === 0) return m.split("/")[1] || fallbackExt || "";
  if (m === "video/mp4") return "mp4";
  if (m === "video/webm") return "webm";
  if (m.indexOf("video/") === 0) return m.split("/")[1] || fallbackExt || "";
  return fallbackExt || "";
}

export function resolveAbsoluteLmUrl(url, lmBase) {
  const s = url != null ? String(url).trim() : "";
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  const base = (lmBase || "").replace(/\/+$/, "");
  if (!base) return s;
  return `${base}${s.charAt(0) === "/" ? "" : "/"}${s}`;
}

export function lmUrlHost(lmBase) {
  const b = (lmBase || "").replace(/\/+$/, "");
  if (!b) return "";
  try {
    return new URL(b.endsWith("/") ? b : `${b}/`).host;
  } catch (e) {
    return "";
  }
}

export function urlHost(absUrl) {
  if (!absUrl) return "";
  try {
    return new URL(absUrl).host;
  } catch (e) {
    return "";
  }
}

export function formatGeneratedTime(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${y}-${m}-${day} ${hh}:${mm}:${ss}`;
}



// WEBPACK FOOTER //
// ./src/views/aigc/aigcCreateHelpers.js